import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Linkedin, Instagram, ArrowUpRight, Loader2, Check } from "lucide-react";
import { LINKEDIN_URL, INSTAGRAM_URL, STRIPE_URL } from "@/lib/links";
import { track } from "@/lib/analytics";
import LogoMark from "@/components/site/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, email y mensaje.");
      return;
    }
    if (!consent) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      track("contact_submit", { has_company: !!form.company });
      toast.success("Mensaje enviado. Te responderé lo antes posible.");
      setForm({ name: "", email: "", company: "", message: "" });
      setConsent(false);
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error("No he podido enviar el mensaje. Vuelve a intentarlo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#06231D] text-[#F7F9F8] pt-24 lg:pt-32 pb-10 overflow-hidden grain"
    >
      <div aria-hidden className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[140px] opacity-30 brand-gradient" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Massive display CTA line */}
        <div className="mb-20 lg:mb-28 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A84C] mb-6">Último paso</div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] max-w-[20ch] mx-auto">
            Empieza por saber dónde <span className="font-serif-italic font-normal text-[#2EC48A]">estás</span>.
          </h2>
          <div className="mt-10 flex justify-center">
            <a
              href={STRIPE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-button"
              onClick={() => track("cta_click", { location: "footer", product: "diagnostic" })}
              className="group inline-flex items-center gap-3 px-8 h-16 rounded-full brand-gradient text-white text-base font-medium cta-lift"
            >
              Descúbrelo aquí
              <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center group-hover:bg-white/25 transition-colors">
                <ArrowUpRight size={18} strokeWidth={1.5} />
              </span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 border-t border-white/10 pt-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <a href="#top" className="flex items-center gap-3">
              <LogoMark size={36} />
              <span className="text-white font-display text-2xl tracking-tight">
                Optimiza<span className="text-[#2EC48A]">RRHH</span>
              </span>
            </a>
            <p className="mt-6 text-white/60 text-base font-light leading-relaxed max-w-md">
              Consultoría asíncrona de gestión de talento para startups tech en España y LATAM.
            </p>

            <div className="mt-10 flex items-center gap-3">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="w-11 h-11 rounded-full border border-white/15 grid place-items-center text-white/70 hover:text-[#C9A84C] hover:border-[#C9A84C] transition-colors"
              >
                <Linkedin size={16} strokeWidth={1.5} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="w-11 h-11 rounded-full border border-white/15 grid place-items-center text-white/70 hover:text-[#C9A84C] hover:border-[#C9A84C] transition-colors"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Right: contact form */}
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 mb-6">
              ¿Prefieres escribirme? — Formulario asíncrono
            </div>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8" data-testid="contact-form">
              <FieldInput label="Nombre" value={form.name} onChange={onChange("name")} testId="contact-name" required />
              <FieldInput label="Email" type="email" value={form.email} onChange={onChange("email")} testId="contact-email" required />
              <div className="md:col-span-2">
                <FieldInput label="Empresa (opcional)" value={form.company} onChange={onChange("company")} testId="contact-company" />
              </div>
              <div className="md:col-span-2">
                <FieldTextarea label="En qué necesitas ayuda" value={form.message} onChange={onChange("message")} testId="contact-message" required />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="contact-consent"
                    className="peer sr-only"
                  />
                  <span
                    aria-hidden
                    className="mt-0.5 w-5 h-5 rounded border border-white/25 grid place-items-center flex-shrink-0 transition-colors peer-checked:border-[#2EC48A] peer-checked:bg-[#2EC48A]"
                  >
                    {consent && <Check size={13} strokeWidth={3} className="text-[#06231D]" />}
                  </span>
                  <span className="text-white/55 text-xs leading-relaxed">
                    He leído y acepto la{" "}
                    <Link to="/privacidad" className="text-[#C9A84C] link-underline" target="_blank">
                      política de privacidad
                    </Link>{" "}
                    y el tratamiento de mis datos para responder a mi consulta.
                  </span>
                </label>
              </div>

              <div className="md:col-span-2 flex items-center justify-between pt-2">
                <p className="text-white/40 text-xs max-w-sm">
                  Al enviar aceptas que te responda a tu email. No spam, nunca.
                </p>
                <button
                  type="submit"
                  disabled={loading || sent}
                  data-testid="contact-submit"
                  className={`group inline-flex items-center gap-3 px-6 h-12 rounded-full border text-sm font-medium transition-colors disabled:opacity-90 ${
                    sent
                      ? "border-[#2EC48A] bg-[#2EC48A] text-[#06231D]"
                      : "border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#06231D]"
                  }`}
                >
                  {loading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : sent ? (
                    <Check size={14} strokeWidth={2.5} />
                  ) : (
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  )}
                  {loading ? "Enviando" : sent ? "¡Enviado!" : "Enviar mensaje"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} OptimizaRRHH · Todos los derechos reservados.</div>
          <div className="flex items-center gap-5">
            <Link to="/privacidad" className="hover:text-[#C9A84C] transition-colors" data-testid="footer-privacy-link">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-[#C9A84C] transition-colors" data-testid="footer-legal-link">Aviso legal</Link>
            <span className="uppercase tracking-[0.2em]">Async · Made in Barcelona</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FieldInput({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block group">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#2EC48A] outline-none text-white text-base py-2 transition-colors"
      />
    </label>
  );
}

function FieldTextarea({ label, value, onChange, required, testId }) {
  return (
    <label className="block group">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">{label}</span>
      <textarea
        value={value}
        onChange={onChange}
        rows={4}
        required={required}
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#2EC48A] outline-none text-white text-base py-2 transition-colors resize-none"
      />
    </label>
  );
}
